import { useEffect, useCallback, useMemo } from "react";
import { isHotkey } from "is-hotkey";
import { useShallow } from "zustand/react/shallow";

import useAnalytics from "hooks/useAnalytics";

import useCommandPaletteStore from "../store/useCommandPaletteStore";
import { getNextIndex, getPreviousIndex } from "../utils/navigation";
import useCommandPaletteExecuteAction from "./useExecuteAction";
import {
  selectNavigableActions,
  selectActiveActionId,
} from "../store/useCommandPaletteStore/selectors";

const useCommandPaletteKeyboard = (): void => {
  const trackAnalyticsEvent = useAnalytics();
  const executeAction = useCommandPaletteExecuteAction();

  const {
    activeActionId,
    setActiveActionId,
    isOpen,
    setModalOpen,
    currentView,
    clearSubList,
    navigableActions,
  } = useCommandPaletteStore(
    useShallow((state) => ({
      activeActionId: selectActiveActionId(state),
      setActiveActionId: state.setActiveActionId,
      isOpen: state.isModalOpen,
      setModalOpen: state.setModalOpen,
      currentView: state.currentView,
      clearSubList: state.clearSubList,
      navigableActions: selectNavigableActions(state),
    }))
  );

  const totalActions = navigableActions.length;

  const currentIndex = useMemo(() => {
    if (!activeActionId) return 0;
    const index = navigableActions.findIndex((action) => action.id === activeActionId);
    return index === -1 ? 0 : index;
  }, [activeActionId, navigableActions]);

  const handleArrowDown = useCallback(() => {
    const nextIndex = getNextIndex(currentIndex, totalActions);
    const nextAction = navigableActions[nextIndex];
    if (nextAction) {
      setActiveActionId(nextAction.id, "keyboard");
    }
  }, [currentIndex, totalActions, navigableActions, setActiveActionId]);

  const handleArrowUp = useCallback(() => {
    const prevIndex = getPreviousIndex(currentIndex, totalActions);
    const prevAction = navigableActions[prevIndex];
    if (prevAction) {
      setActiveActionId(prevAction.id, "keyboard");
    }
  }, [currentIndex, totalActions, navigableActions, setActiveActionId]);

  const handleEnter = useCallback(() => {
    const activeAction = navigableActions[currentIndex];
    if (activeAction) {
      executeAction(activeAction);
      trackAnalyticsEvent("Command palette: keyboard action executed", {
        actionId: activeAction.id,
        actionType: activeAction.type,
        actionTitle: activeAction.title,
      });
    }
  }, [navigableActions, currentIndex, executeAction, trackAnalyticsEvent]);

  const handleEscape = useCallback(() => {
    if (currentView === "sub-list") {
      clearSubList();
    }
  }, [currentView, clearSubList]);

  // Global listener: Cmd+K to open palette
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isHotkey("mod+k", event)) {
        event.preventDefault();
        // Close any open dropdowns/popovers by dispatching Escape key
        document.activeElement?.dispatchEvent(
          new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true })
        );
        setModalOpen(true);
        trackAnalyticsEvent("Command palette opened");
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [setModalOpen, trackAnalyticsEvent]);

  // Modal-only listener: Arrow navigation, Enter, ESC
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          handleArrowDown();
          break;
        case "ArrowUp":
          event.preventDefault();
          handleArrowUp();
          break;
        case "Enter":
          event.preventDefault();
          handleEnter();
          break;
        case "Escape":
          if (currentView === "sub-list") {
            event.preventDefault();
            event.stopPropagation();
            handleEscape();
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, handleArrowDown, handleArrowUp, handleEnter, handleEscape, currentView]);
};

export default useCommandPaletteKeyboard;
